import { del, getApiErrorMessage, getBodyOnly, post, put, putRaw } from "./aws_api_helper";
import * as url from "./url_helper";

const PROGRAM_TYPES = ["EDP", "PPA", "SPP", "RI_SP"];
const LIST_PAGE_SIZE = 100;

const isHttpError = (res) =>
  res && typeof res.status === "number" && res.status >= 400;

const toError = (res) => {
  const err = new Error(getApiErrorMessage(res) || "Request failed.");
  err.status = res?.status;
  err.data = res?.data ?? res;
  return err;
};

const unwrapPost = (res) => {
  if (isHttpError(res)) throw toError(res);
  if (res && res.data !== undefined && typeof res.status === "number") {
    return res.data;
  }
  return res;
};

const unwrapGet = (res) => {
  if (isHttpError(res)) throw toError(res);
  return res;
};

const withQuery = (path, params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

const createdAtMs = (value) => {
  if (!value) return 0;
  const normalized = String(value).includes("T") ? value : String(value).replace(" ", "T");
  const ms = Date.parse(normalized);
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Create a wholesale program (EDP / PPA / SPP / RI_SP).
 * POST /cloudnova/partner-central — body includes program_type; returns full saved record (201).
 */
export const createPartnerProgram = async (body) => {
  const response = await post(url.PARTNER_CENTRAL, body);
  return unwrapPost(response);
};

/**
 * GET /cloudnova/partner-central?program_type=&id= — detail by UUID.
 */
export const getPartnerProgram = async (programType, id) => {
  const response = unwrapGet(
    await getBodyOnly(
      withQuery(url.PARTNER_CENTRAL, {
        program_type: programType,
        id,
      })
    )
  );
  return response;
};

/**
 * PUT /cloudnova/partner-central?program_type=&id=
 */
export const updatePartnerProgram = async (programType, id, body) => {
  const response = await put(
    withQuery(url.PARTNER_CENTRAL, {
      program_type: programType,
      id,
    }),
    body
  );
  return unwrapPost(response);
};

/**
 * Retire a wholesale program (EDP / PPA / SPP / RI_SP).
 * PUT { is_retired: true } → status RETIRED. false reactivates after ACTIVE validation.
 */
export const retirePartnerProgram = async (programType, id, isRetired = true) =>
  updatePartnerProgram(programType, id, { is_retired: Boolean(isRetired) });

/**
 * DELETE /cloudnova/partner-central?program_type=&id= — archives the program.
 */
export const archivePartnerProgram = async (programType, id) => {
  const response = await del(
    withQuery(url.PARTNER_CENTRAL, {
      program_type: programType,
      id,
    })
  );
  if (isHttpError(response)) throw toError(response);
  const body = response?.data;
  if (typeof body === "string") {
    try {
      return body ? JSON.parse(body) : {};
    } catch (_error) {
      return {};
    }
  }
  return body;
};

/**
 * List one program type with offset pagination (no combined four-type list in the API).
 * GET /cloudnova/partner-central?program_type=&status=&limit=&offset=
 */
export const listPartnerProgramsByType = async (programType, { status, limit = LIST_PAGE_SIZE } = {}) => {
  const items = [];
  let offset = 0;
  for (;;) {
    const response = unwrapGet(
      await getBodyOnly(
        withQuery(url.PARTNER_CENTRAL, {
          program_type: programType,
          status,
          limit,
          offset,
        })
      )
    );
    const page = Array.isArray(response?.items) ? response.items : [];
    items.push(...page);
    if (page.length < limit) break;
    offset += limit;
  }
  return items;
};

/**
 * Normalize GET /cloudnova/billing-rules/metadata?kind=eligible_services body to string[].
 * Dev response: { kind, list_type, count, services: [{ display_name, name, mapped, ... }] }.
 * Only services with mapped === true are shown in the UI catalog.
 */
export const normalizeEligibleServicesMetadata = (responseData) => {
  const raw = Array.isArray(responseData)
    ? responseData
    : responseData && typeof responseData === "object"
      ? responseData.services ??
        responseData.values ??
        responseData.eligible_services ??
        responseData.service_codes ??
        []
      : [];
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  raw.forEach((item) => {
    if (!item || typeof item !== "object" || item.mapped !== true) return;
    const label = String(item.display_name ?? item.name ?? "").trim();
    if (!label || seen.has(label)) return;
    seen.add(label);
    out.push(label);
  });
  return out;
};

/**
 * GET /cloudnova/billing-rules/metadata?kind=eligible_services
 */
export const getEligibleServicesMetadata = async () => {
  const response = unwrapGet(
    await getBodyOnly(
      withQuery(url.GET_BILLING_RULES_METADATA, { kind: "eligible_services" })
    )
  );
  return normalizeEligibleServicesMetadata(response);
};

/**
 * List programs across EDP / PPA / SPP / RI_SP.
 * API has no combined list; fetches each type then merges newest-first.
 * When a single status is requested, it is passed to the API for server-side filtering.
 */
export const listPartnerPrograms = async ({ statuses = ["DRAFT", "ACTIVE"] } = {}) => {
  const allowed = new Set(statuses);
  const apiStatus = statuses.length === 1 ? statuses[0] : undefined;
  const pages = await Promise.all(
    PROGRAM_TYPES.map((programType) =>
      listPartnerProgramsByType(programType, apiStatus ? { status: apiStatus } : {})
    )
  );
  return pages
    .flat()
    .filter((item) => item && allowed.has(item.status))
    .sort((a, b) => {
      const byDate = createdAtMs(b.created_at) - createdAtMs(a.created_at);
      if (byDate !== 0) return byDate;
      return String(b.id || "").localeCompare(String(a.id || ""));
    });
};

/**
 * Java onboarding: create real customer organization + commercial profile.
 * POST /organization/customer — 200 plain text (not JSON).
 */
export const createOnboardedCustomer = async (body) => {
  const response = await post(url.POST_CUSTOMER, body);
  if (isHttpError(response)) throw toError(response);
  const data = response?.data;
  const message =
    typeof data === "string"
      ? data
      : typeof data?.message === "string"
        ? data.message
        : "Customer has been created successfully.";
  return { message, raw: data };
};

/**
 * Java PUT /organization/customer — org/contact/address/billing, additive
 * accountNumbers, active flag, and optional partnerCentral commercial updates.
 * 200 plain text.
 */
export const updateCustomerAccounts = async (body) => {
  const response = await putRaw(url.POST_CUSTOMER, body);
  if (isHttpError(response)) throw toError(response);
  const data = response?.data;
  const message =
    typeof data === "string"
      ? data
      : typeof data?.message === "string"
        ? data.message
        : "Customer updated successfully.";
  return { message, raw: data };
};

/** Alias for expanded Java PUT /organization/customer. */
export const updateOrganizationCustomer = updateCustomerAccounts;

/**
 * @deprecated Prefer createOnboardedCustomer (Java POST /organization/customer).
 * Python POST /cloudnova/partner-central/customer is no longer used for create.
 */
export const createPartnerCustomer = async (body) => {
  const response = await post(url.PARTNER_CENTRAL_CUSTOMER, body);
  return unwrapPost(response);
};

/**
 * List Customers with offset pagination.
 * GET /cloudnova/partner-central/customer?status=&limit=&offset=
 */
export const listPartnerCustomers = async ({ statuses = ["DRAFT", "ACTIVE"], limit = LIST_PAGE_SIZE } = {}) => {
  const allowed = new Set(statuses);
  const collect = async (status) => {
    const items = [];
    let offset = 0;
    for (;;) {
      const response = unwrapGet(
        await getBodyOnly(
          withQuery(url.PARTNER_CENTRAL_CUSTOMER, {
            status,
            limit,
            offset,
          })
        )
      );
      const page = Array.isArray(response?.items) ? response.items : [];
      items.push(...page);
      if (page.length < limit) break;
      offset += limit;
    }
    return items;
  };

  // Fetch each requested status (API status filter is single-valued).
  const pages = await Promise.all([...allowed].map((status) => collect(status)));
  return pages
    .flat()
    .filter((item) => item && allowed.has(item.status))
    .sort((a, b) => {
      const byDate = createdAtMs(b.created_at) - createdAtMs(a.created_at);
      if (byDate !== 0) return byDate;
      return Number(b.id || 0) - Number(a.id || 0);
    });
};

/**
 * GET /cloudnova/partner-central/customer?id= — detail by commercial profile BIGINT id.
 */
export const getPartnerCustomer = async (id) => {
  const response = unwrapGet(
    await getBodyOnly(withQuery(url.PARTNER_CENTRAL_CUSTOMER, { id }))
  );
  return response;
};

/**
 * GET /cloudnova/partner-central/customer?customer_orgcode= — detail by org code.
 */
export const getPartnerCustomerByOrgcode = async (customerOrgcode) => {
  const response = unwrapGet(
    await getBodyOnly(
      withQuery(url.PARTNER_CENTRAL_CUSTOMER, { customer_orgcode: customerOrgcode })
    )
  );
  return response;
};

/**
 * Java GET /organization/customer?organization_id= — org identity + nested partnerCentral.
 */
export const getOrganizationCustomer = async (organizationId) => {
  const response = unwrapGet(
    await getBodyOnly(
      withQuery(url.POST_CUSTOMER, { organization_id: organizationId })
    )
  );
  return response;
};

/**
 * After Java onboarding (plain-text response), resolve the commercial profile from the list.
 */
export const findPartnerCustomerProfile = async ({
  organizationName,
  primaryContactEmail,
} = {}) => {
  const items = await listPartnerCustomers({ statuses: ["DRAFT", "ACTIVE"] });
  const name = String(organizationName || "").trim().toLowerCase();
  const email = String(primaryContactEmail || "").trim().toLowerCase();
  if (!name && !email) return null;
  const exact = items.find((item) => {
    const itemName = String(item.organization_name || "").trim().toLowerCase();
    const itemEmail = String(item.primary_contact_email || "").trim().toLowerCase();
    if (name && itemName !== name) return false;
    if (email && itemEmail !== email) return false;
    return true;
  });
  if (exact) return exact;
  if (!name) return null;
  return (
    items.find(
      (item) => String(item.organization_name || "").trim().toLowerCase() === name
    ) || null
  );
};

/**
 * PUT /cloudnova/partner-central/customer?id=
 */
export const updatePartnerCustomer = async (id, body) => {
  const response = await put(
    withQuery(url.PARTNER_CENTRAL_CUSTOMER, { id }),
    body
  );
  return unwrapPost(response);
};

/**
 * DELETE /cloudnova/partner-central/customer?id= — archives the customer.
 */
export const archivePartnerCustomer = async (id) => {
  const response = await del(withQuery(url.PARTNER_CENTRAL_CUSTOMER, { id }));
  if (isHttpError(response)) throw toError(response);
  const body = response?.data;
  if (typeof body === "string") {
    try {
      return body ? JSON.parse(body) : {};
    } catch (_error) {
      return {};
    }
  }
  return body;
};

/**
 * List Task Orders. Optional associated_organization_id filters by real customer organization.id.
 * GET /cloudnova/partner-central/task-order
 */
export const listPartnerTaskOrders = async ({
  associatedOrganizationId,
  statuses = ["DRAFT", "ACTIVE"],
  limit = LIST_PAGE_SIZE,
} = {}) => {
  const allowed = new Set(statuses);
  const collect = async (status) => {
    const items = [];
    let offset = 0;
    for (;;) {
      const response = unwrapGet(
        await getBodyOnly(
          withQuery(url.PARTNER_CENTRAL_TASK_ORDER, {
            associated_organization_id: associatedOrganizationId,
            status,
            limit,
            offset,
          })
        )
      );
      const page = Array.isArray(response?.items) ? response.items : [];
      items.push(...page);
      if (page.length < limit) break;
      offset += limit;
    }
    return items;
  };

  const pages = await Promise.all([...allowed].map((status) => collect(status)));
  return pages
    .flat()
    .filter((item) => item && allowed.has(item.status))
    .sort((a, b) => {
      const byDate = createdAtMs(b.created_at) - createdAtMs(a.created_at);
      if (byDate !== 0) return byDate;
      return String(b.id || "").localeCompare(String(a.id || ""));
    });
};

export const getPartnerTaskOrder = async (id) => {
  const response = unwrapGet(
    await getBodyOnly(withQuery(url.PARTNER_CENTRAL_TASK_ORDER, { id }))
  );
  return response;
};

export const createPartnerTaskOrder = async (body) => {
  const response = await post(url.PARTNER_CENTRAL_TASK_ORDER, body);
  return unwrapPost(response);
};

export const updatePartnerTaskOrder = async (id, body) => {
  const response = await put(
    withQuery(url.PARTNER_CENTRAL_TASK_ORDER, { id }),
    body
  );
  return unwrapPost(response);
};

export const archivePartnerTaskOrder = async (id) => {
  const response = await del(withQuery(url.PARTNER_CENTRAL_TASK_ORDER, { id }));
  if (isHttpError(response)) throw toError(response);
  const body = response?.data;
  if (typeof body === "string") {
    try {
      return body ? JSON.parse(body) : {};
    } catch (_error) {
      return {};
    }
  }
  return body;
};
